import React, { useState } from 'react';
import { ParentAction } from '../../types';
import { namesMatch } from '../../utils/namesMatch';

interface AcknowledgeModalProps {
  open: boolean;
  policyTitle: string;
  /** What is being asked. SIGN requires a typed name; ACKNOWLEDGE is one tap. */
  parentAction: ParentAction;
  /** Present for a parent ack — the child this response is for. */
  childName?: string;
  /** Pre-fills the signature field with the signer's own name. */
  defaultName?: string;
  submitting?: boolean;
  /** Why the last attempt was refused, shown above the buttons. */
  error?: string | null;
  onCancel: () => void;
  onConfirm: (signedName?: string) => void;
}

/**
 * The confirm step for acknowledging or signing a policy.
 *
 * A SIGN response is a compliance record naming a person, so the typed name is
 * required and the statement shown above the button is the exact thing being
 * agreed to. An ACKNOWLEDGE is one tap — it records that the policy was seen,
 * which is a weaker claim and should not pretend otherwise.
 *
 * When a child is named, the title says so: the ask is per child, and a parent
 * with two children needs to know which one they are answering for.
 */
export const AcknowledgeModal: React.FC<AcknowledgeModalProps> = ({
  open,
  policyTitle,
  parentAction,
  childName,
  defaultName = '',
  submitting = false,
  error,
  onCancel,
  onConfirm,
}) => {
  const [signedName, setSignedName] = useState(defaultName);
  const isSign = parentAction === ParentAction.SIGN;
  // The typed name is the signature, and the sentence below renders it
  // verbatim — so it must actually be the signer's name, not merely non-empty.
  // Mirrors the server check (src/policies/utils/names-match.ts); the server
  // is the control, this is just immediate feedback so the user isn't bounced
  // by a round-trip. Tolerant of case, spacing and missing fadas — an Irish
  // parent typing "Sinead" for "Sinéad" is signing their own name.
  const nameMatches = isSign && namesMatch(signedName, defaultName);
  const nameTyped = signedName.trim().length > 0;
  const showMismatch = isSign && nameTyped && !nameMatches;
  const canSubmit = !submitting && (!isSign || nameMatches);

  if (!open) return null;

  return (
    <div
      data-testid="policy-ack-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">
          {isSign ? 'Sign this policy' : 'Confirm you have read this'}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {policyTitle}
          {childName ? ` — for ${childName}` : ''}
        </p>

        {isSign ? (
          <>
            <label
              htmlFor="policy-signed-name"
              className="mt-4 block text-sm font-medium text-gray-700"
            >
              Type your full name
            </label>
            <input
              id="policy-signed-name"
              data-testid="policy-signed-name"
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
              autoComplete="name"
            />
            {showMismatch && (
              <p data-testid="policy-signed-name-mismatch" className="mt-1 text-sm text-red-600">
                Please type your own name, {defaultName}, exactly as it appears on your account.
              </p>
            )}
            <p className="mt-3 text-sm text-gray-700">
              I, <span className="font-medium">{signedName.trim() || '…'}</span>, have read and
              agree to {policyTitle}
              {childName ? ` on behalf of ${childName}` : ''}.
            </p>
            <p data-testid="policy-esign-notice" className="mt-2 text-xs text-gray-500">
              By typing your name you are signing this policy electronically. Your name, the date
              and time, and the version you signed are recorded.
            </p>
          </>
        ) : (
          <p className="mt-4 text-sm text-gray-700">
            This records that you have read {policyTitle}
            {childName ? ` for ${childName}` : ''}.
          </p>
        )}

        {error && (
          <p data-testid="policy-ack-error" role="alert" className="mt-4 text-sm text-red-600">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            data-testid="policy-ack-cancel"
            onClick={onCancel}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="policy-ack-submit"
            disabled={!canSubmit}
            onClick={() => onConfirm(isSign ? signedName.trim() : undefined)}
            className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {isSign ? 'Sign' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};
